import { useEffect, useRef } from 'react';

import { noticesStore } from 'stores/global';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { useStore as useRepathingStore } from '../../repathing/repathingStore';
import { resolveKeyPoseMotionContext } from '../keyPoseMotionUtils';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import type { KeyPoseGenerationHistoryStatus } from '../types';

export function useKeyPoseGenerationRecovery() {
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const repathingStore = useRepathingStore();
  const hasApiKey = keyPoseStore.state.hasApiKey;
  const keyPoseReferenceName =
    repathingStore.state.controlsMode === 'body'
      ? keyPoseStore.state.activeReferenceName
      : null;
  const motionContext = resolveKeyPoseMotionContext({
    scene: motionEditorStore.state.scene,
    playback: motionEditorStore.state.playback,
    keyPoseReferenceName,
  });
  const motionID = motionContext?.motionID ?? null;
  const referenceName = motionContext?.referenceName ?? null;
  const sceneID = motionContext?.sceneID ?? null;
  const timelineIndex = motionContext?.timelineIndex ?? null;
  const historyContextKey =
    sceneID && referenceName && timelineIndex !== null
      ? [sceneID, referenceName, timelineIndex].join('::')
      : null;
  const recoverableJobID =
    keyPoseStore.state.generationHistory.find((entry) =>
      isRecoverableHistoryStatus(entry.status),
    )?.jobID ?? null;
  const activeRecoveryJobID = useRef<string | null>(null);

  useEffect(() => {
    if (
      !hasApiKey ||
      !motionID ||
      !referenceName ||
      !sceneID ||
      timelineIndex === null
    ) {
      keyPoseStore.clearGenerationHistory();
      return;
    }

    void keyPoseStore.loadSceneObjectMotionEditHistory({
      motionID,
      referenceName,
      sceneID,
      timelineIndex,
    });
  }, [
    hasApiKey,
    historyContextKey,
    keyPoseStore,
    motionID,
    referenceName,
    sceneID,
    timelineIndex,
  ]);

  useEffect(() => {
    if (!hasApiKey || !historyContextKey || !recoverableJobID) {
      return;
    }
    if (activeRecoveryJobID.current === recoverableJobID) {
      return;
    }
    if (keyPoseStore.state.generation.status === 'pending') {
      return;
    }

    const entry = keyPoseStore.state.generationHistory.find(
      (historyEntry) => historyEntry.jobID === recoverableJobID,
    );
    if (!entry) {
      return;
    }

    activeRecoveryJobID.current = recoverableJobID;
    void keyPoseStore
      .recoverSceneObjectMotionKeyPoseGeneration({
        entry,
      })
      .then((result) => {
        if (!result.ok) {
          noticesStore.error(result.error);
        }
      })
      .finally(() => {
        if (activeRecoveryJobID.current === recoverableJobID) {
          activeRecoveryJobID.current = null;
        }
      });
  }, [hasApiKey, historyContextKey, keyPoseStore, recoverableJobID]);
}

function isRecoverableHistoryStatus(status: KeyPoseGenerationHistoryStatus) {
  return status === 'queued' || status === 'regenerating';
}
